/**
 * Phone numbers, as typed and as shown.
 *
 * ⚠️ Mirrors `PhoneNumber` on the server, and the server is the authority. Everything here
 * is a courtesy: it lets the form say "include the area code" while somebody is still
 * looking at the field, instead of after they have pressed Submit and lost the step. The
 * shape actually stored is decided server-side, and a browser that skips all of this
 * changes nothing about what lands in the database.
 *
 * ⚠️ A bare ten-digit number is assumed North American, exactly as the server assumes it.
 * ARS is a US organisation. Anything else is typed with a leading `+`.
 */

/** Digits only, so "(561) 555-0142" and "561.555.0142" compare equal. */
function digitsOf(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * Turns what somebody typed into E.164, or null if it is not a number we could ring.
 *
 * Null for blank as well as for malformed, because "no number" is a decision the caller
 * makes: on the request form it is a refusal, on the profile form it clears the field.
 */
export function normalisePhone(input: string | null | undefined): string | null {
  if (!input) return null;

  const trimmed = input.trim();
  if (trimmed === "") return null;

  // Only the shapes people actually type. A letter or a second number in the same box is
  // refused rather than stripped — silently discarding half of what someone typed produces
  // a number that looks saved and dials nowhere.
  if (!/^\+?[\d\s().\-/]+$/.test(trimmed)) return null;

  const digits = digitsOf(trimmed);

  if (trimmed.startsWith("+")) {
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }

  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;

  // A seven-digit local number, deliberately refused rather than guessed at. An area code
  // cannot be inferred from somebody's site — people work across facilities — and a
  // callback that reaches the wrong area code is worse than a form that insists.
  return null;
}

/** Digits only, up to eight. "x4021" and "ext. 4021" are how people write one. */
export function normaliseExtension(input: string | null | undefined): string | null {
  if (!input) return null;

  const digits = digitsOf(input);
  return digits.length >= 1 && digits.length <= 8 ? digits : null;
}

/**
 * E.164 as a person reads it: `+15615550142` becomes `(561) 555-0142`.
 *
 * Presentation only and at the edge, exactly like a timestamp — the stored value is
 * canonical and this never travels back to the server.
 *
 * An international number is left as it arrived. Guessing at another country's grouping
 * produces something that looks authoritative and is wrong, and the E.164 form is
 * universally dialable.
 */
export function formatPhone(e164: string | null | undefined, extension?: string | null): string {
  if (!e164) return "";

  const withExtension = (base: string) => (extension ? `${base} ext. ${extension}` : base);

  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (match) return withExtension(`(${match[1]}) ${match[2]}-${match[3]}`);

  return withExtension(e164);
}

/**
 * The same number as something a VoIP app can dial: `tel:+15615550142;ext=4021`.
 *
 * Null when there is nothing dialable, so a caller renders plain text rather than a link
 * that goes nowhere — a directory number arrives from Azure as whatever somebody typed into
 * it, so "we have a value" and "we have a number" are different facts here.
 *
 * ⚠️ **BUILT FROM THE NORMALISED VALUE, NEVER FROM WHAT WAS TYPED.** `normalisePhone` emits
 * `+` followed by digits and nothing else, and `normaliseExtension` emits one to eight
 * digits — so the URI is assembled from two closed character sets and there is no input that
 * can turn it into a different scheme or smuggle a second target into it. That property is
 * the reason this takes the raw stored value and normalises it here, rather than accepting a
 * pre-formatted string a caller assembled.
 *
 * ⚠️ **`;ext=` is RFC 3966 and is not honoured consistently.** Dialpad and RingCentral both
 * register as `tel:` handlers, and some builds dial the base number and drop the extension
 * without saying so — which connects the agent to a main line believing they are through to
 * a desk. Nothing here can fix that, so the extension stays VISIBLE in the link text
 * (`formatPhone` renders "ext. 4021"): if the app drops it, the person can key it, and the
 * failure is one they can see rather than one that quietly reaches the wrong extension.
 * This is the single place the form is decided, so switching to DTMF pauses after testing
 * against the two apps is one edit.
 */
export function telHref(
  phone: string | null | undefined,
  extension?: string | null,
): string | null {
  const e164 = normalisePhone(phone);
  if (!e164) return null;

  const ext = normaliseExtension(extension);
  return ext ? `tel:${e164};ext=${ext}` : `tel:${e164}`;
}
